import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TContractForm, TSowFrequencies } from "../Contracts.model";
import { SowFrequencyEnum, useContractForm } from "./ContractForm.hooks";

import classes from "./ContractForm.module.scss";
import { classNames as cx } from "primereact/utils";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { Dropdown } from "primereact/dropdown";
import { Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FormFieldsPanel } from "../../../../components/form-fields-panel/form-fields-panel";
import { If } from "../../../../components/If/If";
import {
  CustomerCategoryEnum,
  RecurrenceEnum,
} from "../../SalesLead/Lead.model";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import TextEditor from "./TextEditor";
import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import RecurrenceForm from "../../../../components/RecurrenceForm";
import { recurrenceOptions, petsOptions } from "./config";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { useQuery } from "@tanstack/react-query";
import { requests } from "../../../../services/http-common";
import {
  applyPreSignedDefaults,
  resolveContractEndDate,
  getSowFrequencyValue,
  sanitizeSowFrequencies,
  upsertSowFrequency,
} from "./ContractForm.utils";

type ContractFormProps = {
  onSubmit: (
    props: TContractForm,
    preSigned?: { file: File; signedDate: Date }
  ) => void;
  onSaveDraft?: (props: Partial<TContractForm>) => void;
  leadId: string;
  isPending: boolean;
  isDraftPending?: boolean;
  contractId?: string;
};

type TScopeOfWorkWithFrequency = {
  index: number;
  sowId: number;
  frequency: SowFrequencyEnum;
};

export const ContractForm = ({
  onSubmit,
  onSaveDraft,
  leadId,
  isPending,
  isDraftPending,
  contractId,
}: ContractFormProps) => {
  const navigate = useNavigate();
  const [allSowsSelected, setAllSowsSelected] = useState<boolean>(false);
  const [selectedSOWs, setSelectedSOWs] = useState<TScopeOfWorkWithFrequency[]>(
    []
  );
  const [selectedScopeOfWorks, setSelectedScopeOfWorks] = useState<number[]>(
    []
  );
  const [weekDays, setWeekDays] = useState<number[]>([]);
  const [hasInitializedEditState, setHasInitializedEditState] =
    useState<boolean>(false);
  const [isPreSigned, setIsPreSigned] = useState(false);
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [signedDate, setSignedDate] = useState<Date | null>(null);
  const [preSignedError, setPreSignedError] = useState<string | null>(null);

  const {
    hookForm,
    leadPayload,
    contractTypeOptions,
    floorSurfaceOptions,
    suppliesOptions,
    paperProductOptions,
    isContractDataLoaded
  } = useContractForm(leadId, contractId, isPreSigned);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = hookForm;

  const [contractTypeId, recurrence, frequency] = watch([
    "contractTypeId",
    "recurrence",
    "frequency",
  ]);

  const selectedContractTypeHasSowTemplate = !!contractTypeOptions?.find(
    (option: any) => String(option.value ?? option.id) === String(contractTypeId)
  )?.hasSowTemplate;

  const handleDownloadSowTemplate = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DSC_SERVICE_URL}/contract-type/${contractTypeId}/sow-template`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "sow-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // no-op: no existing toast/error mechanism in this file for this action
    }
  };

  const { data: scopeOfWorkOptionsData } = useQuery({
    queryKey: ["scope-of-work-options", contractTypeId],
    enabled: !!contractTypeId,
    queryFn: () => {
      return requests.get(
        "/scope-of-work?rows=1000&contractTypeId=" + contractTypeId
      );
    },
  });

  const scopeOfWorkOptions = useMemo(() => {
    if (!scopeOfWorkOptionsData) return [];

    return scopeOfWorkOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [scopeOfWorkOptionsData]);

  const sowFrequencies = watch("sowFrequencies") || [];
  const sanitizeContractFormValues = (
    values: TContractForm | Partial<TContractForm>
  ) => {
    const scopeOfWorkIds = (values.scopeOfWorkIds || selectedScopeOfWorks || []).map(Number);

    return {
      ...values,
      scopeOfWorkIds,
      sowFrequencies: sanitizeSowFrequencies(
        values.sowFrequencies || [],
        scopeOfWorkIds
      ),
    };
  };

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK`
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleWeekDayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const dayValue = Number(e.currentTarget.getAttribute("data-day-index"));

    let newWeekDays;
    if (weekDays.includes(dayValue)) {
      newWeekDays = weekDays.filter((day) => day !== dayValue);
    } else {
      newWeekDays = [...weekDays, dayValue];
    }

    setWeekDays(newWeekDays);

    setValue("daysOfWeek", newWeekDays);
  };

  const handleAllSowsChange = (event: { checked: boolean }) => {
    const isChecked = event.checked;
    setAllSowsSelected(isChecked);

    if (isChecked) {
      // Select all checkboxes
      const newSelectedSOWs = scopeOfWorkOptions.map(
        (option: any, index: number) => {
          const existingIndex = selectedScopeOfWorks.indexOf(option.value);
          if (existingIndex !== -1) {
            return selectedSOWs[existingIndex];
          } else {
            return {
              index,
              frequency: SowFrequencyEnum.PER_VISIT,
              sowId: option.value,
            };
          }
        }
      );

      setSelectedSOWs(newSelectedSOWs);
      setSelectedScopeOfWorks(
        scopeOfWorkOptions.map((option: any) => option.value)
      );
    } else {
      // Deselect all checkboxes
      setSelectedSOWs([]);
      setSelectedScopeOfWorks([]);
    }
  };

  // useEffect(()=> {
  //     const newSelectedSOWs = scopeOfWorkOptions.map(
  //       (option: any, index: number) => {
  //         const existingIndex = selectedScopeOfWorks.indexOf(option.value);
  //         if (existingIndex !== -1) {
  //           return selectedSOWs[existingIndex];
  //         } else {
  //           return {
  //             index,
  //             frequency: SowFrequencyEnum.PER_VISIT,
  //             sowId: option.value,
  //           };
  //         }
  //       }
  //     );

  //     setSelectedSOWs(newSelectedSOWs);
  // } , [])


  const handleScopeOfWorkCheckboxChange = (
    event: { checked: boolean },
    id: number,
    index: number
  ) => {


    const isChecked = event.checked;
    if (isChecked) {
      setSelectedSOWs([
        ...selectedSOWs,
        {
          index,
          frequency: SowFrequencyEnum.PER_VISIT,
          sowId: id,
        },
      ]);

      setSelectedScopeOfWorks([...selectedScopeOfWorks, id]);
    } else {

      setSelectedSOWs(
        selectedSOWs.filter((selectedSow) => selectedSow.sowId !== id)
      );

      setSelectedScopeOfWorks(
        selectedScopeOfWorks.filter((selectedId) => selectedId !== id)
      );
    }
  };

  // const handleFrequencyChange = (
  //   e: { target: { value: string } },
  //   index: number
  // ) => {
  //   const frequency = e.target.value;

  //   // console.log("poer-->" , frequency , index , selectedSOWs)

  //   const sowIndex = selectedSOWs.findIndex((sow) => sow.index === index);

  //   // console.log(sowIndex , "perre->>>>")

  //   if (sowIndex !== -1) {
  //     const updatedSOWs = [...selectedSOWs];

  //     updatedSOWs[sowIndex] = {
  //       ...updatedSOWs[sowIndex],
  //       frequency: frequency as SowFrequencyEnum,
  //     };

  //     setSelectedSOWs(updatedSOWs);
  //   }
  // };

  // useEffect(() => {

  //   const requestSows: TSowFrequencies[] = selectedSOWs.map((selectedSow) => {
  //     return {
  //       sowId: selectedSow?.sowId,
  //       frequency: systemName(selectedSow?.frequency) as SowFrequencyEnum,
  //     };
  //   });

  //   setValue("scopeOfWorkIds", selectedScopeOfWorks);
  //   setValue("sowFrequencies", requestSows);
  // }, [selectedSOWs]);

  //   useEffect(() => {
  //   const existing = watch("sowFrequencies") || [];

  //   const sowFrequencyData = selectedSOWs.map((s) => {
  //   const found = existing.find((x) => x.sowId === s.sowId);
  //   return {
  //     sowId: s.sowId,
  //     frequency: found?.frequency || s.frequency || SowFrequencyEnum.PER_VISIT,
  //   };
  // });

  //   setValue("scopeOfWorkIds", selectedScopeOfWorks);
  //   setValue("sowFrequencies", sowFrequencyData);
  // }, [selectedSOWs]);

  // Minimal drop-in replacement.
  // This should replace the effect that currently rebuilds sowFrequencies from selectedSOWs.
  // Key idea: read the current form values and preserve them, only add/remove entries for the selectedScopeOfWorks.

  useEffect(() => {
    // read existing frequencies from the form (most reliable)
    const existing: TSowFrequencies[] = hookForm.getValues
      ? (hookForm.getValues("sowFrequencies") as TSowFrequencies[]) || []
      : (watch("sowFrequencies") as TSowFrequencies[]) || [];

    // build quick lookup of existing frequencies by sowId
    const map = new Map<number, SowFrequencyEnum>();
    existing.forEach((f) => {
      if (f?.sowId != null) map.set(Number(f.sowId), f.frequency as SowFrequencyEnum);
    });

    // Build the updated array only for currently selected SOW ids (preserve existing values)
    const updated: TSowFrequencies[] = selectedScopeOfWorks.map((sowId) => ({
      sowId,
      frequency: map.has(sowId) ? map.get(sowId)! : SowFrequencyEnum.PER_VISIT,
    }));

    // sync scopeOfWorkIds and sowFrequencies to the form
    setValue("scopeOfWorkIds", selectedScopeOfWorks);
    setValue("sowFrequencies", updated);
  }, [selectedScopeOfWorks]); // run only when selectedScopeOfWorks changes (checkboxes)


  useEffect(() => {
    if (!isContractDataLoaded || hasInitializedEditState) return;

    const loadedScopeOfWorkIds = (
      hookForm.getValues("scopeOfWorkIds") || []
    ).map(Number);
    const loadedSowFrequencies =
      (hookForm.getValues("sowFrequencies") as TSowFrequencies[]) || [];
    const loadedWeekDays = (hookForm.getValues("daysOfWeek") || []).map(
      Number
    );

    setSelectedScopeOfWorks(loadedScopeOfWorkIds);
    setSelectedSOWs(
      loadedScopeOfWorkIds.map((sowId, index) => ({
        index,
        sowId,
        frequency:
          (getSowFrequencyValue(loadedSowFrequencies, sowId) as SowFrequencyEnum) ||
          SowFrequencyEnum.PER_VISIT,
      }))
    );
    setWeekDays(loadedWeekDays);
    setHasInitializedEditState(true);
  }, [hookForm, isContractDataLoaded, hasInitializedEditState]);


  useEffect(() => {
  if (scopeOfWorkOptions.length === 0) return;

  // If every SOW is selected → Select All must be checked
  setAllSowsSelected(
    selectedScopeOfWorks.length > 0 &&
    selectedScopeOfWorks.length === scopeOfWorkOptions.length
  );
}, [selectedScopeOfWorks, scopeOfWorkOptions]);

  const recurrenceEndDate = watch("recurrenceEndDate");

  // A recurring contract ends when its recurrence ends - one date, shown here
  // read-only so it is still visible but cannot drift from "Ends By".
  const endDateFollowsRecurrence = recurrence === RecurrenceEnum.Y;




  return (
    <form
      onSubmit={handleSubmit((values) => {
        if (isPreSigned && (!signedFile || !signedDate)) {
          setPreSignedError("Select the signed document and its signing date.");
          return;
        }
        setPreSignedError(null);
        const sanitized = resolveContractEndDate(sanitizeContractFormValues(values));
        onSubmit(
          (isPreSigned
            ? applyPreSignedDefaults(sanitized)
            : sanitized) as TContractForm,
          isPreSigned && signedFile && signedDate
            ? { file: signedFile, signedDate }
            : undefined
        );
      })}
      className={cx(classes.form)}
    >
      <FormFieldsPanel
        label="Contract information"
        className={classes.clientInfoContainer}
      >
        <div className={classes.fields}>
          <LabelValue label="Project Type" isRequired>
            <Controller
              name="contractTypeId"
              control={control}
              render={({ field }) => (
                <Dropdown
                  appendTo="self"
                  id={field.name}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value !== field.value) {
                      setSelectedSOWs([]);
                      setSelectedScopeOfWorks([]);
                      setAllSowsSelected(false);
                      setValue("scopeOfWorkIds", []);
                      setValue("sowFrequencies", []);
                    }
                    field.onChange(e.value);
                  }}
                  options={contractTypeOptions}
                  placeholder="Select type"
                  className={cx({
                    "p-invalid": errors.contractTypeId,
                  })}
                />
              )}
            />
            {getFormErrorMessage("contractTypeId")}
          </LabelValue>
          <LabelValue label="Notice Required For Cancellation" isRequired={!isPreSigned}>
            <Controller
              name="cancellation"
              render={({ field }) => (
                <InputText
                  {...field}
                  keyfilter="pint"
                  placeholder="Notice Required For Cancellation"
                  className={cx({ "p-invalid": errors.cancellation })}
                />
              )}
              control={control}
            />
            {getFormErrorMessage("cancellation")}
          </LabelValue>
          <LabelValue label="Amount" isRequired={!isPreSigned}>
            <Controller
              name="amount"
              render={({ field }) => (
                <InputNumber
                  value={field.value}
                  onValueChange={({ value }) => field.onChange(value)}
                  inputId="currency-us"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  min={0}
                  placeholder="Amount"
                  className={cx({ "p-invalid": errors.amount }, classes.amount)}
                />
              )}
              control={control}
            />
            {getFormErrorMessage("amount")}
          </LabelValue>
          <LabelValue
            label="Compensation"
            isRequired={!isPreSigned}
            className={classes.compensation}
          >
            <div style={{ width: "100%" }}>
              <Controller
                name="compensation"
                render={({ field }) => {
                  return (
                    <TextEditor
                      value={field.value}
                      onTextChange={(e) => {
                        field.onChange(e.htmlValue);
                      }}
                      placeholder="Compensation"
                    />
                  );
                }}
                control={control}
              />
            </div>
            {getFormErrorMessage("compensation")}
          </LabelValue>
          <LabelValue label="Contract end date">
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <Calendar
                  id={field.name}
                  value={
                    endDateFollowsRecurrence
                      ? recurrenceEndDate
                        ? new Date(recurrenceEndDate)
                        : undefined
                      : field.value
                      ? new Date(field.value)
                      : undefined
                  }
                  onChange={(e) => field.onChange((e.value as Date) ?? null)}
                  dateFormat="mm/dd/yy"
                  showIcon
                  disabled={endDateFollowsRecurrence}
                  placeholder={
                    endDateFollowsRecurrence
                      ? "Set by the recurrence"
                      : "Contract end date"
                  }
                />
              )}
            />
            <small className={classes.fieldHint}>
              <If condition={endDateFollowsRecurrence}>
                <>
                  Follows the recurrence&rsquo;s &ldquo;Ends By&rdquo; date. Sales is alerted 60
                  days before it.
                </>
                <>
                  Sales is alerted 60 days before this date. Leave empty for no renewal alert.
                </>
              </If>
            </small>
          </LabelValue>
        </div>
        <If condition={!contractId}>
          <div className={classes.preSignedRow}>
            <LabelValue label="Existing signed contract">
              <Checkbox
                checked={isPreSigned}
                onChange={(e) => setIsPreSigned(!!e.checked)}
              />
            </LabelValue>
            <If condition={isPreSigned}>
              <>
                <LabelValue label="Signed document" isRequired>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setSignedFile(e.target.files?.[0] ?? null)
                    }
                  />
                </LabelValue>
                <LabelValue label="Date signed" isRequired>
                  <Calendar
                    value={signedDate ?? undefined}
                    onChange={(e) => setSignedDate((e.value as Date) ?? null)}
                    dateFormat="mm/dd/yy"
                    maxDate={new Date()}
                    showIcon
                  />
                </LabelValue>
                <div>
                  <Button
                    type="button"
                    label="Download SOW template"
                    icon="pi pi-download"
                    className="p-button-text"
                    disabled={!contractTypeId || !selectedContractTypeHasSowTemplate}
                    onClick={handleDownloadSowTemplate}
                  />
                  {contractTypeId && !selectedContractTypeHasSowTemplate ? (
                    <small className={classes.fieldHint}>
                      No template uploaded for this contract type.
                    </small>
                  ) : null}
                </div>
                {preSignedError ? (
                  <small className="p-error">{preSignedError}</small>
                ) : null}
              </>
            </If>
          </div>
        </If>
      </FormFieldsPanel>

      <FormFieldsPanel
        label="Client information"
        className={classes.clientInfoContainer}
      >
        <div className={classes.clientInfo}>
          <div className={classes.infoColumn}>
            <If
              condition={
                leadPayload?.customerCategory ===
                CustomerCategoryEnum.INDIVIDUAL
              }
            >
              <>
                <LabelValue label="First Name">
                  <InputText value={leadPayload?.name} disabled />
                </LabelValue>
                <LabelValue label="Last Name">
                  <InputText value={leadPayload?.surname} disabled />
                </LabelValue>
              </>
              <>
                <LabelValue label="Company Name">
                  <InputText value={leadPayload?.companyName} disabled />
                </LabelValue>
                <LabelValue label="Contract Name">
                  <InputText value={leadPayload?.contactName} disabled />
                </LabelValue>
              </>
            </If>
          </div>
          <div className={classes.infoColumn}>
            <LabelValue label="Address">
              <InputText value={leadPayload?.address} disabled />
            </LabelValue>
          </div>
          <div className={classes.infoColumn}>
            <LabelValue label="Phone number">
              <InputText value={leadPayload?.phone} disabled />
            </LabelValue>
          </div>
        </div>
      </FormFieldsPanel>
      <FormFieldsPanel
        label="OPERATIONS INFORMATION"
        className={classes.clientInfoContainer}
      >
        <div className={classes.clientInfo}>
          <div className={classes.infoColumn}>
            <LabelValue label="Estimated SQFT" isRequired={!isPreSigned}>
              <Controller
                name="estimatedSQFT"
                render={({ field }) => (
                  <InputNumber
                    tabIndex={25}
                    value={field.value}
                    onValueChange={(e) => field.onChange(e.value)}
                    placeholder="Estimated SQFT"
                    className={cx({
                      "p-invalid": errors.estimatedSQFT,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("estimatedSQFT")}
            </LabelValue>
          </div>

          <div className={classes.infoColumn}>
            <LabelValue label="Type of Space" isRequired={!isPreSigned}>
              <Controller
                name="typeOfSpace"
                render={({ field }) => (
                  <InputText
                    {...field}
                    tabIndex={28}
                    placeholder="Type of Space"
                    className={cx({ "p-invalid": errors.typeOfSpace })}
                    style={{ marginBottom: 10 }}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("typeOfSpace")}
            </LabelValue>
          </div>

          <div className={classes.infoColumn}>
            <LabelValue label="Type of Floor Surfaces" isRequired={!isPreSigned}>
              <Controller
                name="typeOfFloorSurfaceIds"
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    appendTo="self"
                    tabIndex={31}
                    options={floorSurfaceOptions}
                    placeholder="Type of floor surfaces"
                    className={cx({
                      "p-invalid": errors.typeOfFloorSurfaceIds,
                    })}
                    style={{ width: "230px" }}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("typeOfFloorSurfaceIds")}
            </LabelValue>
          </div>

          <If condition={recurrence === RecurrenceEnum.Y}>
            <div className={classes.infoColumn}>
              {/* TODO: handle editing here */}
              <RecurrenceForm
                control={control}
                daysOfWeek={weekDays}
                errors={errors}
                frequency={frequency}
                getFormErrorMessage={getFormErrorMessage}
                handleWeekDayClick={handleWeekDayClick}
              />
            </div>
          </If>

          <div className={classes.infoColumn}>
            <LabelValue label="Supplies" isRequired={!isPreSigned}>
              <Controller
                name="suppliesIds"
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    tabIndex={26}
                    appendTo="self"
                    options={suppliesOptions}
                    placeholder="Supplies"
                    className={cx({
                      "p-invalid": errors.suppliesIds,
                    })}
                    style={{ width: "230px" }}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("suppliesIds")}
            </LabelValue>
          </div>

          <div className={classes.infoColumn}>
            <LabelValue label="Pets" isRequired={!isPreSigned}>
              <Controller
                name="pets"
                render={({ field }) => {
                  return (
                    <Dropdown
                      {...field}
                      appendTo="self"
                      tabIndex={29}
                      onChange={(option) => field.onChange(option.value)}
                      options={petsOptions}
                      ariaLabel="pets"
                      placeholder="Select a Type"
                      className={cx({
                        "p-invalid": errors.pets,
                      })}
                    />
                  );
                }}
                control={control}
              />
              {getFormErrorMessage("pets")}
            </LabelValue>
          </div>

          <div className={classes.infoColumn}>
            <LabelValue label="Paper Products" isRequired={!isPreSigned}>
              <Controller
                name="paperProductIds"
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    appendTo="self"
                    tabIndex={27}
                    options={paperProductOptions}
                    placeholder="Paper products"
                    className={cx({
                      "p-invalid": errors.paperProductIds,
                    })}
                    style={{ width: "230px" }}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("paperProductIds")}
            </LabelValue>
          </div>

          <div className={classes.infoColumn}>
            <LabelValue label="Recurrence" isRequired={!isPreSigned}>
              <Controller
                name="recurrence"
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    appendTo="self"
                    onChange={(option) => field.onChange(option.value)}
                    options={recurrenceOptions}
                    ariaLabel="recurrence"
                    tabIndex={30}
                    placeholder="Select a Recurrence"
                    className={cx({
                      "p-invalid": errors.recurrence,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("recurrence")}
            </LabelValue>
          </div>
        </div>
      </FormFieldsPanel>

      <FormFieldsPanel
        label="Operations information"
        className={classes.clientInfoContainer}
      >
        <div
          className={classes.checkbox}
          style={{ padding: 10, paddingBottom: 15 }}
        >
          <Checkbox checked={allSowsSelected} onChange={handleAllSowsChange} />
          <label>Select All</label>
        </div>
        
        <div className={classes.operationsnfo}>
          <DataTable
            value={scopeOfWorkOptions}
            totalRecords={scopeOfWorkOptions.length}
          >
            <Column
              field="name"
              header="Areas and Tasks"
              body={(rowData, props) => (
                <>
                  <div className={cx(classes.checkbox, classes.selectall)}>
                    <Checkbox
                      inputId={rowData.value}
                      checked={selectedScopeOfWorks.includes(rowData.value)}
                      onChange={(e) =>
                        handleScopeOfWorkCheckboxChange(
                          e,
                          rowData.value,
                          props.rowIndex
                        )
                      }
                    />
                    <label>{rowData.label}</label>
                  </div>
                </>
              )}
            />
            <Column
              header="Frequency"
              body={(rowData, props) => (
                <Controller
                  name="sowFrequencies"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Dropdown
                        appendTo="self"
                        id={field.name}
                        // value={dropDownData?.frequency}
                        // value={
                        //   selectedSOWs.find(
                        //     (item) => item?.index === props?.rowIndex
                        //   )?.frequency || SowFrequencyEnum.PER_VISIT
                        // }
                        value={
                          getSowFrequencyValue(sowFrequencies, rowData.value)
                          || SowFrequencyEnum.PER_VISIT
                        }

                        onChange={(e) => {
                          setValue(
                            "sowFrequencies",
                            upsertSowFrequency(sowFrequencies, rowData.value, e.value)
                          );
                        }}
                        // onChange={(e) =>
                        //   handleFrequencyChange(e, props.rowIndex)
                        // }
                        options={Object.values(SowFrequencyEnum).map((v) => ({
                          label: v,
                          value: v,
                        }))}
                        style={{ minWidth: "203px" }}
                        disabled={!selectedScopeOfWorks.includes(rowData.value)}
                      />
                    );
                  }}
                />
              )}
            />
          </DataTable>
           <div style={{ marginTop: 6 }}>
    {getFormErrorMessage("scopeOfWorkIds")}
  </div>
        </div>
      </FormFieldsPanel>
      <div className={classes.actions}>
        <Button
          label="Cancel"
          type="reset"
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate(-1)}
        />
        {onSaveDraft ? (
          <Button
            label="Save Draft"
            type="button"
            className="p-button-outlined p-button-secondary"
            loading={isDraftPending}
            disabled={isPending}
            onClick={() =>
              onSaveDraft(
                resolveContractEndDate(sanitizeContractFormValues(hookForm.getValues()))
              )
            }
          />
        ) : null}
        <Button
          label="Submit Contract"
          type="submit"
          disabled={isPending}
          loading={isPending}
        />
      </div>
    </form>
  );
};
